import { useEffect, useState } from "react";
import { supabase } from "@vinoga/db";
import { Truck, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";

const sectionClass = "bg-card border border-border rounded-lg p-5 space-y-4";
const inputClass =
  "w-full bg-card border border-border rounded-lg px-3 py-2 font-body text-sm text-foreground outline-none focus:border-primary placeholder:text-muted-foreground";
const labelClass = "font-body text-xs font-medium text-muted-foreground mb-1 block";

// Per-provider config persisted to courier_settings.config (jsonb).
// Numbers are stored in cents to keep arithmetic exact; the UI shows
// EUR with two decimals.
type CourierConfig = {
  priceCents?: number;
  pakomatsPriceCents?: number;
  freeAboveCents?: number;
  /** false hides outdoor parcel lockers from checkout; pickup shops stay. */
  lockersEnabled?: boolean;
};

type Setting = {
  id: string;
  provider: "venipak";
  active: boolean;
  display_name: string;
  config: CourierConfig;
  updated_at: string;
};

const PROVIDER_HINTS: Partial<Record<string, { credEnv: string; description: string }>> = {
  venipak: {
    credEnv: "VENIPAK_USER / VENIPAK_PASS / VENIPAK_USER_ID",
    description:
      "Venipak parcel + home delivery. Per-store sender block comes from inventory_locations.",
  },
};

// Cents ↔ display. Empty input becomes undefined so we don't persist
// a noisy 0 — courier_settings.config stays minimal.
function centsToEur(cents: number | undefined): string {
  if (cents == null) return "";
  return (cents / 100).toFixed(2);
}
function eurToCents(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === "") return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100);
}

export function CouriersSettings() {
  const { toast } = useToast();
  const t = useT();

  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("courier_settings")
        .select("*")
        .order("display_name");
      if (cancelled) return;
      if (error) {
        toast({
          title: t("couriersSettings.errorLoading", "Error loading couriers"),
          description: error.message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      setSettings((data ?? []) as Setting[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [t, toast]);

  const update = (id: string, patch: Partial<Setting>) => {
    setSettings((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };
  const updateConfig = (id: string, patch: Partial<CourierConfig>) => {
    setSettings((prev) =>
      prev.map((s) => (s.id === id ? { ...s, config: { ...s.config, ...patch } } : s)),
    );
  };

  const save = async (s: Setting) => {
    setSavingId(s.id);
    // Strip undefined keys so courier_settings.config stays small
    // and the admin doesn't see ghost "priceCents: null" entries
    // appear in the DB.
    const config: CourierConfig = {};
    if (s.config.priceCents != null) config.priceCents = s.config.priceCents;
    if (s.config.pakomatsPriceCents != null)
      config.pakomatsPriceCents = s.config.pakomatsPriceCents;
    if (s.config.freeAboveCents != null) config.freeAboveCents = s.config.freeAboveCents;
    if (s.config.lockersEnabled === false) config.lockersEnabled = false;

    const { error } = await supabase
      .from("courier_settings")
      .update({ active: s.active, display_name: s.display_name, config })
      .eq("id", s.id);
    if (error) {
      toast({
        title: t("common.saveFailed", "Save failed"),
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({ title: t("couriersSettings.saved", "Courier saved") });
    }
    setSavingId(null);
  };

  if (loading) {
    return (
      <div className="p-4 font-body text-sm text-muted-foreground">
        {t("common.loading", "Loading...")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-xl font-bold text-foreground">
          {t("couriersSettings.title", "Couriers")}
        </h1>
        <p className="font-body text-xs text-muted-foreground">
          {t(
            "couriersSettings.subtitle",
            "Toggle each courier on/off + set checkout pricing. The storefront only offers active providers. API credentials live in env vars (Railway), not here."
          )}
        </p>
      </div>

      <div className="space-y-4">
        {settings.map((s) => {
          const hint = PROVIDER_HINTS[s.provider];
          return (
            <section key={s.id} className={sectionClass}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Truck size={18} className="text-muted-foreground" />
                  <div>
                    <h2 className="font-display text-sm font-bold text-foreground">
                      {s.display_name}
                    </h2>
                    <p className="font-mono text-xs text-muted-foreground">{s.provider}</p>
                  </div>
                  {s.active ? (
                    <Badge variant="success">
                      {t("couriersSettings.statusOn", "Enabled")}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">
                      {t("couriersSettings.statusOff", "Disabled")}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={s.active}
                    onCheckedChange={(v) => update(s.id, { active: v })}
                  />
                  <Button size="sm" onClick={() => save(s)} disabled={savingId === s.id}>
                    <Save size={14} />
                    {savingId === s.id
                      ? t("common.saving", "Saving…")
                      : t("common.save", "Save")}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 border-t border-border pt-3 sm:grid-cols-3">
                <div>
                  <label className={labelClass}>
                    {t("couriersSettings.priceCourier", "Courier price (€)")}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={centsToEur(s.config.priceCents)}
                    onChange={(e) =>
                      updateConfig(s.id, { priceCents: eurToCents(e.target.value) })
                    }
                    placeholder="4.95"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {t("couriersSettings.pricePakomats", "Pakomāts price (€)")}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={centsToEur(s.config.pakomatsPriceCents)}
                    onChange={(e) =>
                      updateConfig(s.id, {
                        pakomatsPriceCents: eurToCents(e.target.value),
                      })
                    }
                    placeholder="2.95"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {t("couriersSettings.freeAbove", "Free shipping above (€)")}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={centsToEur(s.config.freeAboveCents)}
                    onChange={(e) =>
                      updateConfig(s.id, { freeAboveCents: eurToCents(e.target.value) })
                    }
                    placeholder="50.00"
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-4 border-t border-border pt-3">
                <div>
                  <div className="font-body text-sm font-medium text-foreground">
                    {t("couriersSettings.lockersEnabled", "Show parcel lockers at checkout")}
                  </div>
                  <p className="font-body text-xs text-muted-foreground">
                    {t(
                      "couriersSettings.lockersEnabledHint",
                      "When off, outdoor parcel lockers are hidden from checkout. Pickup shops stay available."
                    )}
                  </p>
                </div>
                <Switch
                  checked={s.config.lockersEnabled !== false}
                  onCheckedChange={(v) => updateConfig(s.id, { lockersEnabled: v })}
                />
              </div>

              {hint && (
                <div className="space-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
                  <div>
                    <span className="font-medium">
                      {t("couriersSettings.credentials", "Credentials env vars:")}
                    </span>{" "}
                    <span className="font-mono">{hint.credEnv}</span>
                  </div>
                  <div>{hint.description}</div>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
